import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Play, Radio, Monitor, Smartphone, Volume2, VolumeX, CheckCircle, XCircle, RotateCcw, ArrowRight } from 'lucide-react';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';

const GAME_STATES = {
  READY: 'ready',
  DEVICE_SELECT: 'device_select',
  LISTENING: 'listening',
  QUESTIONS: 'questions',
  FINISHED: 'finished',
};

// Device Mode Selection Component
const DeviceModeSelector = ({ onSelect }) => (
  <Card className="border-slate-200">
    <CardContent className="pt-8">
      <div className="text-center mb-6">
        <h2 className="font-heading text-xl font-bold text-[#0B1D51] mb-2">
          Select Your Device
        </h2>
        <p className="text-sm text-[#64748B]">
          Choose how you're taking this test for the best experience
        </p>
      </div>
      
      <div className="grid grid-cols-2 gap-4">
        <Button
          variant="outline"
          className="h-32 flex-col gap-3 hover:border-[#0B1D51] hover:bg-[#0B1D51]/5"
          onClick={() => onSelect('desktop')}
          data-testid="select-desktop"
        >
          <Monitor className="w-10 h-10 text-[#0B1D51]" />
          <div>
            <p className="font-semibold">Laptop/Desktop</p>
            <p className="text-xs text-[#64748B]">Best audio quality</p>
          </div>
        </Button>
        
        <Button
          variant="outline"
          className="h-32 flex-col gap-3 hover:border-[#FF9F1C] hover:bg-[#FF9F1C]/5"
          onClick={() => onSelect('mobile')}
          data-testid="select-mobile"
        >
          <Smartphone className="w-10 h-10 text-[#FF9F1C]" />
          <div>
            <p className="font-semibold">Mobile/Tablet</p>
            <p className="text-xs text-[#64748B]">Touch controls</p>
          </div>
        </Button>
      </div>
    </CardContent>
  </Card>
);

// Fisher-Yates shuffle
const shuffleArray = (array) => {
  const shuffled = [...array];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
};

// Message scenarios with correct answers marked as object property
const MESSAGE_SCENARIOS = {
  1: [
    {
      message: "Driver of train one yankee five zero, this is Control. Your train will now depart from platform 3 instead of platform 5. I repeat, platform 3 instead of platform 5. Please acknowledge.",
      questions: [
        {
          question: "What is the train's headcode?",
          options: [
            { text: "1Y50", correct: true },
            { text: "1Y05", correct: false },
            { text: "5Y10", correct: false },
            { text: "1Y55", correct: false }
          ]
        },
        {
          question: "Which platform should the train now depart from?",
          options: [
            { text: "Platform 5", correct: false },
            { text: "Platform 3", correct: true },
            { text: "Platform 1", correct: false },
            { text: "Platform 2", correct: false }
          ]
        },
        {
          question: "Which platform was originally assigned?",
          options: [
            { text: "Platform 3", correct: false },
            { text: "Platform 1", correct: false },
            { text: "Platform 5", correct: true },
            { text: "Platform 2", correct: false }
          ]
        }
      ]
    },
    {
      message: "Attention driver of two charlie four five. Your departure time has been revised from fourteen thirty to fourteen forty five. That's fourteen forty five departure. Please confirm.",
      questions: [
        {
          question: "What is the train's headcode?",
          options: [
            { text: "2C54", correct: false },
            { text: "2C45", correct: true },
            { text: "4C25", correct: false },
            { text: "2C44", correct: false }
          ]
        },
        {
          question: "What is the new departure time?",
          options: [
            { text: "14:30", correct: false },
            { text: "14:40", correct: false },
            { text: "14:45", correct: true },
            { text: "14:50", correct: false }
          ]
        },
        {
          question: "What was the original departure time?",
          options: [
            { text: "14:45", correct: false },
            { text: "14:15", correct: false },
            { text: "14:30", correct: true },
            { text: "14:00", correct: false }
          ]
        }
      ]
    },
    {
      message: "Driver of five mike two zero, please reduce speed to 40 miles per hour between Millbrook Junction and Oakfield Station due to track maintenance. Maximum speed 40 miles per hour. Acknowledge please.",
      questions: [
        {
          question: "What is the speed restriction?",
          options: [
            { text: "30 mph", correct: false },
            { text: "40 mph", correct: true },
            { text: "50 mph", correct: false },
            { text: "20 mph", correct: false }
          ]
        },
        {
          question: "Where does the speed restriction start?",
          options: [
            { text: "Oakfield Station", correct: false },
            { text: "Millbrook Junction", correct: true },
            { text: "Central Station", correct: false },
            { text: "Westbrook", correct: false }
          ]
        },
        {
          question: "What is the reason for the speed restriction?",
          options: [
            { text: "Signalling fault", correct: false },
            { text: "Track maintenance", correct: true },
            { text: "Weather conditions", correct: false },
            { text: "Points failure", correct: false }
          ]
        }
      ]
    }
  ],
  2: [
    {
      message: "Control to driver of three whiskey seven five. Low adhesion reported between mile post 42 and mile post 47. Apply sanders as required and anticipate extended braking distances. Current rail head conditions are poor. Message ends.",
      questions: [
        {
          question: "What is the train's headcode?",
          options: [
            { text: "3W57", correct: false },
            { text: "3W75", correct: true },
            { text: "7W35", correct: false },
            { text: "3W77", correct: false }
          ]
        },
        {
          question: "Between which mile posts is low adhesion reported?",
          options: [
            { text: "42 to 47", correct: true },
            { text: "44 to 47", correct: false },
            { text: "42 to 45", correct: false },
            { text: "40 to 47", correct: false }
          ]
        },
        {
          question: "What action should the driver take?",
          options: [
            { text: "Reduce speed to 20mph", correct: false },
            { text: "Apply sanders as required", correct: true },
            { text: "Stop and report", correct: false },
            { text: "Continue normally", correct: false }
          ]
        },
        {
          question: "What should the driver anticipate?",
          options: [
            { text: "Delayed departure", correct: false },
            { text: "Extended braking distances", correct: true },
            { text: "Platform change", correct: false },
            { text: "Route diversion", correct: false }
          ]
        }
      ]
    },
    {
      message: "Urgent message for driver of one hotel nine zero. Signal C-H-47 at Riverdale Junction is showing a failure. You are authorised to pass signal C-H-47 at danger under the instructions of signaller Jenkins, reference number alpha bravo seven two. Proceed at caution not exceeding 15 miles per hour to the next signal.",
      questions: [
        {
          question: "Which signal has failed?",
          options: [
            { text: "CH47", correct: true },
            { text: "CH74", correct: false },
            { text: "CH45", correct: false },
            { text: "CH57", correct: false }
          ]
        },
        {
          question: "What is the maximum permitted speed?",
          options: [
            { text: "10 mph", correct: false },
            { text: "15 mph", correct: true },
            { text: "20 mph", correct: false },
            { text: "25 mph", correct: false }
          ]
        },
        {
          question: "What is the authority reference number?",
          options: [
            { text: "AB27", correct: false },
            { text: "AB72", correct: true },
            { text: "BA72", correct: false },
            { text: "AB77", correct: false }
          ]
        },
        {
          question: "Who authorised passing the signal at danger?",
          options: [
            { text: "Signaller Williams", correct: false },
            { text: "Signaller Jenkins", correct: true },
            { text: "Control Centre", correct: false },
            { text: "Signaller Johnson", correct: false }
          ]
        }
      ]
    }
  ],
  3: [
    {
      message: "Control calling driver of two foxtrot eight five. Change of headcode. Your train will now run as five foxtrot eight five, that's five foxtrot eight five instead of two foxtrot eight five. Additionally, you will make an unscheduled stop at Thornbury at approximately sixteen twenty. Please update your on-board systems and acknowledge.",
      questions: [
        {
          question: "What is the new headcode?",
          options: [
            { text: "2F85", correct: false },
            { text: "5F85", correct: true },
            { text: "5F58", correct: false },
            { text: "8F55", correct: false }
          ]
        },
        {
          question: "What was the original headcode?",
          options: [
            { text: "5F85", correct: false },
            { text: "2F58", correct: false },
            { text: "2F85", correct: true },
            { text: "5F82", correct: false }
          ]
        },
        {
          question: "Where is the unscheduled stop?",
          options: [
            { text: "Thornfield", correct: false },
            { text: "Thornbury", correct: true },
            { text: "Thornton", correct: false },
            { text: "Thorneycroft", correct: false }
          ]
        },
        {
          question: "What approximate time is the stop scheduled for?",
          options: [
            { text: "16:00", correct: false },
            { text: "16:20", correct: true },
            { text: "16:30", correct: false },
            { text: "16:25", correct: false }
          ]
        },
        {
          question: "What action must the driver take?",
          options: [
            { text: "Contact the guard", correct: false },
            { text: "Update on-board systems", correct: true },
            { text: "Wait for further instructions", correct: false },
            { text: "Reduce speed", correct: false }
          ]
        }
      ]
    },
    {
      message: "Priority message. Driver of four alpha three zero, overhead line issues reported between Greenfield Junction and Ashworth Tunnel. Power has been isolated between overhead line equipment sections 15 and 19. Do not enter this section until further notice. You will be held at signal G-F-12 until power is restored. Estimated restoration time is seventeen forty five.",
      questions: [
        {
          question: "Where are the overhead line issues?",
          options: [
            { text: "Greenfield to Ashworth", correct: true },
            { text: "Ashfield to Greenworth", correct: false },
            { text: "Greenfield to Ashbridge", correct: false },
            { text: "Ashworth to Greenfield", correct: false }
          ]
        },
        {
          question: "Which overhead line equipment sections are affected?",
          options: [
            { text: "15 to 18", correct: false },
            { text: "14 to 19", correct: false },
            { text: "15 to 19", correct: true },
            { text: "16 to 19", correct: false }
          ]
        },
        {
          question: "At which signal will the train be held?",
          options: [
            { text: "GF21", correct: false },
            { text: "GF12", correct: true },
            { text: "GF15", correct: false },
            { text: "GF11", correct: false }
          ]
        },
        {
          question: "What is the estimated power restoration time?",
          options: [
            { text: "17:30", correct: false },
            { text: "17:45", correct: true },
            { text: "17:50", correct: false },
            { text: "17:40", correct: false }
          ]
        },
        {
          question: "What must the driver NOT do?",
          options: [
            { text: "Reduce speed", correct: false },
            { text: "Enter the affected section", correct: true },
            { text: "Contact control", correct: false },
            { text: "Make announcements", correct: false }
          ]
        }
      ]
    }
  ],
  4: [
    {
      message: "Control to driver of one papa seven five. Multiple updates. First, your routing has changed - you will now take the Westbury loop via platform 2 at Meadowbank. Second, there is a temporary speed restriction of 30 miles per hour between mile post 67 and mile post 71 due to emergency engineering works. Third, your connection at Riverside has been cancelled, so there is no requirement to hold for the eighteen fifteen from Eastfield. Acknowledge all three points please.",
      questions: [
        {
          question: "Which loop will the train now take?",
          options: [
            { text: "Eastbury loop", correct: false },
            { text: "Westbury loop", correct: true },
            { text: "Southbury loop", correct: false },
            { text: "Northbury loop", correct: false }
          ]
        },
        {
          question: "Which platform at Meadowbank?",
          options: [
            { text: "Platform 1", correct: false },
            { text: "Platform 2", correct: true },
            { text: "Platform 3", correct: false },
            { text: "Platform 4", correct: false }
          ]
        },
        {
          question: "What is the speed restriction?",
          options: [
            { text: "25 mph", correct: false },
            { text: "30 mph", correct: true },
            { text: "35 mph", correct: false },
            { text: "40 mph", correct: false }
          ]
        },
        {
          question: "Between which mile posts is the restriction?",
          options: [
            { text: "65 to 71", correct: false },
            { text: "67 to 71", correct: true },
            { text: "67 to 73", correct: false },
            { text: "69 to 71", correct: false }
          ]
        },
        {
          question: "What connection has been cancelled?",
          options: [
            { text: "18:00 from Westfield", correct: false },
            { text: "18:15 from Eastfield", correct: true },
            { text: "18:15 from Westfield", correct: false },
            { text: "18:30 from Eastfield", correct: false }
          ]
        },
        {
          question: "How many separate points require acknowledgement?",
          options: [
            { text: "Two", correct: false },
            { text: "Three", correct: true },
            { text: "Four", correct: false },
            { text: "Five", correct: false }
          ]
        }
      ]
    }
  ],
  5: [
    {
      message: "Emergency broadcast to all trains on the northern region. Severe weather alert in effect from eighteen hundred hours. High winds expected exceeding 60 miles per hour. All drivers must reduce speed to maximum 50 miles per hour until further notice. Additionally, a temporary speed restriction of 20 miles per hour applies to all viaducts and exposed locations including Ridgemont Viaduct, Highfield Crossing and Summit Bridge. Report any damage or debris sightings immediately to Control using code WEATHER-RED. This restriction applies until zero six hundred hours tomorrow. Control out.",
      questions: [
        {
          question: "What time does the severe weather alert begin?",
          options: [
            { text: "17:00", correct: false },
            { text: "18:00", correct: true },
            { text: "19:00", correct: false },
            { text: "20:00", correct: false }
          ]
        },
        {
          question: "What is the general maximum speed during the alert?",
          options: [
            { text: "40 mph", correct: false },
            { text: "50 mph", correct: true },
            { text: "60 mph", correct: false },
            { text: "30 mph", correct: false }
          ]
        },
        {
          question: "What speed applies to viaducts and exposed locations?",
          options: [
            { text: "15 mph", correct: false },
            { text: "20 mph", correct: true },
            { text: "25 mph", correct: false },
            { text: "30 mph", correct: false }
          ]
        },
        {
          question: "Which of these locations has a 20 mph restriction?",
          options: [
            { text: "Ridgemont Station", correct: false },
            { text: "Ridgemont Viaduct", correct: true },
            { text: "Ridgemont Junction", correct: false },
            { text: "Ridgemont Tunnel", correct: false }
          ]
        },
        {
          question: "What code should be used to report damage?",
          options: [
            { text: "WEATHER-AMBER", correct: false },
            { text: "WEATHER-RED", correct: true },
            { text: "STORM-RED", correct: false },
            { text: "ALERT-RED", correct: false }
          ]
        },
        {
          question: "Until what time does the restriction apply?",
          options: [
            { text: "04:00 tomorrow", correct: false },
            { text: "06:00 tomorrow", correct: true },
            { text: "08:00 tomorrow", correct: false },
            { text: "Midnight", correct: false }
          ]
        }
      ]
    }
  ]
};

const CommunicationGame = ({ level, onComplete }) => {
  const [gameState, setGameState] = useState(GAME_STATES.DEVICE_SELECT);
  const [deviceMode, setDeviceMode] = useState(null);
  const [currentScenarioIndex, setCurrentScenarioIndex] = useState(0);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState([]);
  const [showFeedback, setShowFeedback] = useState(false);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [hasListened, setHasListened] = useState(false);
  const startTime = useRef(Date.now());
  const speechRef = useRef(null);

  // Prepare scenarios with shuffled question options - once per session
  const preparedScenarios = useMemo(() => {
    const levelScenarios = MESSAGE_SCENARIOS[level] || MESSAGE_SCENARIOS[1];
    return levelScenarios.map(scenario => ({
      ...scenario,
      questions: scenario.questions.map(q => ({
        ...q,
        shuffledOptions: shuffleArray(q.options)
      }))
    }));
  }, [level]);

  // Cleanup speech on unmount
  useEffect(() => {
    return () => {
      if (speechRef.current) {
        window.speechSynthesis.cancel();
      }
    };
  }, []);

  const currentScenario = preparedScenarios[currentScenarioIndex];
  const totalQuestions = preparedScenarios.reduce((sum, s) => sum + s.questions.length, 0);
  const questionsAnswered = answers.length;

  const handleDeviceSelect = (mode) => {
    setDeviceMode(mode);
    setGameState(GAME_STATES.READY);
  };

  const startGame = () => {
    startTime.current = Date.now();
    setGameState(GAME_STATES.LISTENING);
    setHasListened(false);
  };

  const playMessage = () => {
    if (isPlaying) {
      window.speechSynthesis.cancel();
      setIsPlaying(false);
      return;
    }

    setIsPlaying(true);
    const utterance = new SpeechSynthesisUtterance(currentScenario.message);
    utterance.rate = 0.9;
    utterance.pitch = 1;
    
    // Try to get a British voice
    const voices = window.speechSynthesis.getVoices();
    const britishVoice = voices.find(v => v.lang === 'en-GB') || voices[0];
    if (britishVoice) {
      utterance.voice = britishVoice;
    }

    utterance.onend = () => {
      setIsPlaying(false);
      setHasListened(true);
    };

    speechRef.current = utterance;
    window.speechSynthesis.speak(utterance);
  };

  const proceedToQuestions = () => {
    window.speechSynthesis.cancel();
    setIsPlaying(false);
    setGameState(GAME_STATES.QUESTIONS);
    setCurrentQuestion(0);
  };

  const handleAnswerSelect = (option, displayIndex) => {
    if (showFeedback) return;
    
    setSelectedAnswer(displayIndex);
    setShowFeedback(true);
    
    const isCorrect = option.correct;
    const newAnswer = {
      scenarioIndex: currentScenarioIndex,
      questionIndex: currentQuestion,
      selectedOption: option,
      displayIndex,
      isCorrect
    };
    
    const updatedAnswers = [...answers, newAnswer];
    setAnswers(updatedAnswers);

    setTimeout(() => {
      const scenarioQuestions = currentScenario.questions;
      
      if (currentQuestion + 1 >= scenarioQuestions.length) {
        // Move to next scenario or finish
        if (currentScenarioIndex + 1 >= preparedScenarios.length) {
          finishGame(updatedAnswers);
        } else {
          setCurrentScenarioIndex(s => s + 1);
          setCurrentQuestion(0);
          setHasListened(false);
          setGameState(GAME_STATES.LISTENING);
        }
      } else {
        setCurrentQuestion(q => q + 1);
      }
      
      setSelectedAnswer(null);
      setShowFeedback(false);
    }, 1500);
  };

  const finishGame = (finalAnswers) => {
    setGameState(GAME_STATES.FINISHED);
    const timeSpent = Math.round((Date.now() - startTime.current) / 1000);
    const correctCount = finalAnswers.filter(a => a.isCorrect).length;
    const totalQ = finalAnswers.length;
    const accuracy = Math.round((correctCount / totalQ) * 100);
    
    onComplete({
      score: correctCount * 10,
      maxScore: totalQ * 10,
      accuracy,
      correct: correctCount,
      wrong: totalQ - correctCount,
      timeSpent,
      answers: finalAnswers
    });
  };

  const restartGame = () => {
    setGameState(GAME_STATES.DEVICE_SELECT);
    setCurrentScenarioIndex(0);
    setCurrentQuestion(0);
    setAnswers([]);
    setSelectedAnswer(null);
    setShowFeedback(false);
    setHasListened(false);
    setIsPlaying(false);
  };

  // Device selection
  if (gameState === GAME_STATES.DEVICE_SELECT) {
    return <DeviceModeSelector onSelect={handleDeviceSelect} />;
  }

  // Ready state
  if (gameState === GAME_STATES.READY) {
    return (
      <Card className="border-slate-200">
        <CardContent className="pt-8">
          <div className="text-center max-w-md mx-auto">
            <div className="w-16 h-16 bg-[#0B1D51]/5 rounded-full flex items-center justify-center mx-auto mb-4">
              <Radio className="w-8 h-8 text-[#0B1D51]" />
            </div>
            <h2 className="font-heading text-2xl font-bold text-[#0B1D51] mb-2">
              Communication Comprehension
            </h2>
            <p className="text-[#64748B] mb-1">
              Level {level} - {preparedScenarios.length} message{preparedScenarios.length > 1 ? 's' : ''} to process
            </p>
            <p className="text-xs text-[#FF9F1C] font-medium mb-6">
              Develops skills to improve performance on MMI Communication Assessment
            </p>
            <div className="bg-[#F8FAFC] rounded-lg p-4 mb-6 text-left">
              <h3 className="font-semibold text-[#0B1D51] mb-2">Instructions:</h3>
              <ul className="text-sm text-[#64748B] space-y-1">
                <li>• Listen carefully to each radio message</li>
                <li>• You can replay the message as needed</li>
                <li>• When ready, proceed to answer questions</li>
                <li>• Focus on specific details: times, locations, numbers</li>
              </ul>
            </div>
            <Button 
              onClick={startGame}
              className="bg-[#0B1D51] hover:bg-[#0B1D51]/90"
              data-testid="start-game-btn"
            >
              <Play className="w-5 h-5 mr-2" />
              Start Assessment
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Listening state
  if (gameState === GAME_STATES.LISTENING) {
    return (
      <Card className="border-slate-200">
        <CardContent className="pt-8">
          <div className="text-center max-w-lg mx-auto">
            <Badge variant="outline" className="mb-4">
              Message {currentScenarioIndex + 1} of {preparedScenarios.length}
            </Badge>
            
            <div className={`w-24 h-24 rounded-full flex items-center justify-center mx-auto mb-6 transition-all ${
              isPlaying ? 'bg-[#2CB67D] animate-pulse' : 'bg-[#0B1D51]/10'
            }`}>
              {isPlaying ? (
                <Volume2 className="w-12 h-12 text-white" />
              ) : (
                <VolumeX className="w-12 h-12 text-[#0B1D51]" />
              )}
            </div>
            
            <h3 className="font-heading text-xl font-semibold text-[#0B1D51] mb-4">
              Radio Message
            </h3>
            
            <p className="text-[#64748B] mb-6">
              {isPlaying ? 'Listening to message...' : hasListened ? 'Message complete. Replay or proceed.' : 'Press play to hear the message'}
            </p>
            
            <div className="flex flex-col gap-3">
              <Button
                onClick={playMessage}
                variant={isPlaying ? "destructive" : "outline"}
                size="lg"
                className="w-full"
                data-testid="play-message-btn"
              >
                {isPlaying ? (
                  <>
                    <VolumeX className="w-5 h-5 mr-2" />
                    Stop
                  </>
                ) : (
                  <>
                    <Volume2 className="w-5 h-5 mr-2" />
                    {hasListened ? 'Replay Message' : 'Play Message'}
                  </>
                )}
              </Button>
              
              {hasListened && (
                <Button
                  onClick={proceedToQuestions}
                  size="lg"
                  className="w-full bg-[#0B1D51] hover:bg-[#0B1D51]/90"
                  data-testid="proceed-btn"
                >
                  Proceed to Questions
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              )}
            </div>
            
            {/* Show message text for accessibility/fallback */}
            <div className="mt-6 p-4 bg-slate-100 rounded-lg text-left">
              <p className="text-xs text-[#64748B] mb-2">Message transcript (for accessibility):</p>
              <p className="text-sm text-[#334155] italic">"{currentScenario.message}"</p>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Questions state
  if (gameState === GAME_STATES.QUESTIONS) {
    const question = currentScenario.questions[currentQuestion];
    const correctCount = answers.filter(a => a.isCorrect).length;
    
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <Badge variant="outline">
            Question {questionsAnswered + 1} of {totalQuestions}
          </Badge>
          <div className="text-sm text-[#64748B]">
            Correct: <span className="font-bold text-[#2CB67D]">{correctCount}</span>
          </div>
        </div>
        
        <div className="h-2 bg-slate-200 rounded-full overflow-hidden">
          <motion.div 
            className="h-full bg-[#0B1D51]"
            animate={{ width: `${((questionsAnswered + 1) / totalQuestions) * 100}%` }}
          />
        </div>
        
        <Card className="border-slate-200">
          <CardContent className="pt-6">
            <p className="text-sm text-[#64748B] mb-2">Message {currentScenarioIndex + 1}</p>
            <h3 className="text-lg font-semibold text-[#0B1D51] mb-6">
              {question.question}
            </h3>
            
            <div className="space-y-3">
              {question.shuffledOptions.map((option, index) => {
                const isSelected = selectedAnswer === index;
                const isCorrect = option.correct;
                const showResult = showFeedback && (isSelected || isCorrect);
                
                let buttonStyle = 'border-slate-200 hover:border-[#0B1D51] hover:bg-[#0B1D51]/5';
                if (showResult) {
                  if (isCorrect) {
                    buttonStyle = 'border-[#2CB67D] bg-[#2CB67D]/10';
                  } else if (isSelected && !isCorrect) {
                    buttonStyle = 'border-red-500 bg-red-500/10';
                  }
                }
                
                return (
                  <motion.button
                    key={index}
                    onClick={() => handleAnswerSelect(option, index)}
                    disabled={showFeedback}
                    className={`w-full p-4 text-left rounded-lg border-2 transition-all ${buttonStyle}`}
                    whileTap={{ scale: 0.98 }}
                    data-testid={`answer-${index}`}
                  >
                    <div className="flex items-center gap-3">
                      <span className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center font-mono text-sm">
                        {String.fromCharCode(65 + index)}
                      </span>
                      <span className="flex-1 text-[#334155]">{option.text}</span>
                      {showResult && isCorrect && <CheckCircle className="w-5 h-5 text-[#2CB67D]" />}
                      {showResult && isSelected && !isCorrect && <XCircle className="w-5 h-5 text-red-500" />}
                    </div>
                  </motion.button>
                );
              })}
            </div>
          </CardContent>
        </Card>
        
        <AnimatePresence>
          {showFeedback && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              className={`p-4 rounded-lg text-center font-medium ${
                question.shuffledOptions[selectedAnswer]?.correct
                  ? 'bg-[#2CB67D] text-white'
                  : 'bg-red-500 text-white'
              }`}
            >
              {question.shuffledOptions[selectedAnswer]?.correct ? (
                <span className="flex items-center justify-center gap-2">
                  <CheckCircle className="w-5 h-5" /> Correct!
                </span>
              ) : (
                <span className="flex items-center justify-center gap-2">
                  <XCircle className="w-5 h-5" /> Incorrect
                </span>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    );
  }

  // Finished state
  if (gameState === GAME_STATES.FINISHED) {
    const correctCount = answers.filter(a => a.isCorrect).length;
    const wrongCount = answers.length - correctCount;
    const accuracy = Math.round((correctCount / answers.length) * 100);
    
    return (
      <Card className="border-slate-200">
        <CardContent className="pt-8">
          <div className="text-center">
            <div className="w-16 h-16 bg-[#2CB67D]/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-8 h-8 text-[#2CB67D]" />
            </div>
            <h2 className="font-heading text-2xl font-bold text-[#0B1D51] mb-2">
              Assessment Complete!
            </h2>
            <div className="grid grid-cols-2 gap-4 max-w-sm mx-auto mt-6">
              <div className="bg-[#F8FAFC] rounded-lg p-4">
                <p className="text-2xl font-bold text-[#0B1D51]">{correctCount * 10}</p>
                <p className="text-sm text-[#64748B]">Score</p>
              </div>
              <div className="bg-[#F8FAFC] rounded-lg p-4">
                <p className="text-2xl font-bold text-[#2CB67D]">{accuracy}%</p>
                <p className="text-sm text-[#64748B]">Accuracy</p>
              </div>
              <div className="bg-[#F8FAFC] rounded-lg p-4">
                <p className="text-2xl font-bold text-[#2CB67D]">{correctCount}</p>
                <p className="text-sm text-[#64748B]">Correct</p>
              </div>
              <div className="bg-[#F8FAFC] rounded-lg p-4">
                <p className="text-2xl font-bold text-[#EF4444]">{wrongCount}</p>
                <p className="text-sm text-[#64748B]">Incorrect</p>
              </div>
            </div>
            <Button 
              onClick={restartGame}
              variant="outline"
              className="mt-6"
              data-testid="restart-game-btn"
            >
              <RotateCcw className="w-4 h-4 mr-2" />
              Try Again
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  return null;
};

export default CommunicationGame;
